// arise-frontend/src/pages/RegisterPage.jsx
// Backend rules (auth/schemas.py UserCreate): email <=255, username 3-100,
// password 12-128, full_name <=200 (optional). The tier is never sent: the
// server always creates FREE accounts. The password is never persisted.
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthCard, { AuthField, AuthAlert } from '../components/auth/AuthCard';
import { register, authErrorMessage, fieldErrorsFrom } from '../services/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_KEYS = ['email', 'username', 'password', 'full_name'];

function validate({ email, username, password, fullName }) {
  const errs = {};
  const e = email.trim();
  const u = username.trim();
  if (!e) errs.email = 'Enter your email address.';
  else if (e.length > 255 || !EMAIL_RE.test(e)) errs.email = 'Enter a valid email address.';
  if (u.length < 3) errs.username = 'Username must be at least 3 characters.';
  else if (u.length > 100) errs.username = 'Username must be at most 100 characters.';
  if (password.length < 12) errs.password = 'Password must be at least 12 characters.';
  else if (password.length > 128) errs.password = 'Password must be at most 128 characters.';
  if (fullName.trim().length > 200) errs.full_name = 'Full name must be at most 200 characters.';
  return errs;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state && typeof location.state.from === 'string' ? location.state.from : undefined;

  const [email, setEmail]       = useState('');
  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors]     = useState({});
  const [formError, setFormError]   = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    const found = validate({ email, username, password, fullName });
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length) return;

    const cleanEmail = email.trim();
    const payload = { email: cleanEmail, username: username.trim(), password };
    if (fullName.trim()) payload.full_name = fullName.trim();

    setSubmitting(true);
    try {
      await register(payload);
      navigate('/login', { replace: true, state: { registeredEmail: cleanEmail, from } });
    } catch (err) {
      setFormError(authErrorMessage(err, 'register'));
      if (err && err.status === 422) {
        const fe = fieldErrorsFrom(err);
        const mapped = {};
        FIELD_KEYS.forEach(k => { if (fe[k]) mapped[k] = fe[k]; });
        setErrors(mapped);
      }
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Create Account"
      subtitle="Register to analyse files with ARISE"
      footer={
        <>
          <span>
            Already registered?{' '}
            <Link to="/login" state={{ from }} style={{ color: '#00f0ff' }}>Log in</Link>
          </span>
          <Link to="/" style={{ color: '#6b8fa8' }}>← Back to home</Link>
        </>
      }
    >
      {formError && <AuthAlert kind="error">{formError}</AuthAlert>}

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          id="reg-email" label="Email" type="email" name="email"
          autoComplete="email" inputMode="email" maxLength={255}
          value={email} onChange={ev => setEmail(ev.target.value)}
          error={errors.email} disabled={submitting}
        />
        <AuthField
          id="reg-username" label="Username" name="username"
          autoComplete="username" maxLength={100} hint="3–100 characters"
          value={username} onChange={ev => setUsername(ev.target.value)}
          error={errors.username} disabled={submitting}
        />
        <AuthField
          id="reg-fullname" label="Full name" name="full_name" optional
          autoComplete="name" maxLength={200}
          value={fullName} onChange={ev => setFullName(ev.target.value)}
          error={errors.full_name} disabled={submitting}
        />
        <AuthField
          id="reg-password" label="Password" type="password" name="password" reveal
          autoComplete="new-password" maxLength={128} hint="12–128 characters"
          value={password} onChange={ev => setPassword(ev.target.value)}
          error={errors.password} disabled={submitting}
        />
        <button
          type="submit" className="btn-primary" disabled={submitting}
          style={{ width: '100%', marginTop: '0.5rem' }}
        >
          {submitting ? 'Creating account…' : 'Create Account'}
        </button>
      </form>
    </AuthCard>
  );
}
